import { BaseAPICallsHandler } from "../BaseAPICallsHandler";
import { NetworkUtils } from "../NetworkUtils";
import { ServerConfig } from "../ServerConfig";

export interface CategorySuggestion {
    _id: string;
    categoryName: string;
    subcategoryName: string;
    email: string;
    comments?: string;
    isRead?: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface GGReview {
    _id: string;
    rating: number;
    comments: string;
    submittedByUserType: string;
    userId: string;
    isRead?: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface WriteToUs {
    _id: string;
    fullName: string;
    contactNo: string;
    email: string;
    message: string;
    isRead?: boolean;
    createdAt: string;
    updatedAt: string;
}

export class ServiceDBAdminAPIHandler {
    public static shared = new ServiceDBAdminAPIHandler();

    apiHandler = BaseAPICallsHandler.shared;
    serverConfig = ServerConfig.shared;
    networkUtils = NetworkUtils.shared;

    constructor();
    constructor(apiHandler: BaseAPICallsHandler, serverConfig: ServerConfig, networkUtils: NetworkUtils);
    public constructor(...params: any[]) {
        if (params.length >= 3) {
            this.apiHandler = params[0];
            this.serverConfig = params[1];
            this.networkUtils = params[2];
        }
    }

    // -------- Category Suggestions --------

    public async getAllCategorySuggestions(): Promise<CategorySuggestion[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.allCategorySuggestionsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.suggestions;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getUnreadCategorySuggestions(): Promise<CategorySuggestion[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.unreadCategorySuggestionsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.suggestions;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getReadCategorySuggestions(): Promise<CategorySuggestion[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.readCategorySuggestionsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.suggestions;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async markCategorySuggestionAsRead(id: string): Promise<CategorySuggestion> {
        const url = this.serverConfig.serviceDBAdminAPIs.markCategorySuggestionReadAPI(id);
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makePatchRequest(headers, url, {});
            return response.data.suggestion;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    // -------- GG Reviews --------

    public async getAllGGReviews(): Promise<GGReview[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.allGGReviewsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.reviews;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getUnreadGGReviews(): Promise<GGReview[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.unreadGGReviewsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.reviews;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getReadGGReviews(): Promise<GGReview[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.readGGReviewsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.reviews;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getGGReviewsByRating(rating: number): Promise<GGReview[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.ggReviewsByRatingAPI(rating);
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.reviews;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async markGGReviewAsRead(id: string): Promise<GGReview> {
        const url = this.serverConfig.serviceDBAdminAPIs.markGGReviewReadAPI(id);
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makePatchRequest(headers, url, {});
            return response.data.review;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    // -------- Write To Us --------

    public async getAllWriteToUs(): Promise<WriteToUs[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.allWriteToUsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.messages;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getUnreadWriteToUs(): Promise<WriteToUs[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.unreadWriteToUsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.messages;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getReadWriteToUs(): Promise<WriteToUs[]> {
        const url = this.serverConfig.serviceDBAdminAPIs.readWriteToUsAPI;
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makeGetRequest(headers, url);
            return response.data.messages;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async markWriteToUsAsRead(id: string): Promise<WriteToUs> {
        const url = this.serverConfig.serviceDBAdminAPIs.markWriteToUsReadAPI(id);
        const headers = this.networkUtils.addAdminAuthHeaders({});
        try {
            const response = await this.apiHandler.makePatchRequest(headers, url, {});
            return response.data.message;
        } catch (err) {
            return Promise.reject(err);
        }
    }
}
